import React, { useState, useEffect, useMemo, useCallback } from "react";
import Card from "./Card";
import AdvertisingHeader from "./AdvertisingHeader";
import axios from "../lib/axios";
import BrandEndorsementQuotationModal from "./BrandEndorsementQuotationModal";
import { formatFieldValue } from "../utils/formatters";

const PAGE_SIZE = 12;

const TIER_VALUES = ["tier1", "tier2", "tier3", "rural"];
const MEDIA_TYPE_VALUES = ["digital", "static"];
const PRICE_BUCKETS = [
  { label: "Under ₹25,000", gte: null, lte: 25000 },
  { label: "₹25,000 - ₹1,00,000", gte: 25000, lte: 100000 },
  { label: "₹1,00,000 - ₹5,00,000", gte: 100000, lte: 500000 },
  { label: "Above ₹5,00,000", gte: 500000, lte: null },
];

const SORT_PARAM_MAP = {
  rank: "rank",
  name: "title",
  price_low: "offer_rate",
  price_high: "-offer_rate",
};

const getLocationLabel = (loc) =>
  formatFieldValue(loc?.city || loc?.state || loc?.country || "") || "";

const OutdoorItemsBrowser = ({ slug }) => {
  const [categoryDoc, setCategoryDoc] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemsError, setItemsError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: PAGE_SIZE,
  });
  const [sortBy, setSortBy] = useState("rank");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [illuminations, setIlluminations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch the category doc by slug
  useEffect(() => {
    let active = true;
    const fetchCategory = async () => {
      setLoading(true);
      setError(null);
      setCategoryDoc(null);
      setItems([]);
      try {
        const url = `/api/outdoor-categories?where[slug][equals]=${encodeURIComponent(
          slug,
        )}&depth=2&limit=1`;
        const response = await axios.get(url);
        if (!active) return;
        if (response.status === 200) {
          const doc = response.data.docs?.[0];
          if (!doc || doc.enabled === false) {
            setError("No details found for this category");
          } else {
            setCategoryDoc(doc);
          }
        } else {
          setError(response.data.message || "Failed to fetch details");
        }
      } catch (err) {
        if (!active) return;
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching details"
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchCategory();
    return () => {
      active = false;
    };
  }, [slug]);

  // Fetch reference lists used to translate filter labels -> relationship ids
  useEffect(() => {
    let active = true;
    const fetchReferenceData = async () => {
      try {
        const [illumRes, locRes] = await Promise.all([
          axios.get(`/api/illuminations?limit=100&depth=0`).catch(() => null),
          axios.get(`/api/locations?limit=500&depth=0&sort=city`).catch(() => null),
        ]);
        if (!active) return;
        if (illumRes?.status === 200) {
          setIlluminations(illumRes.data.docs || []);
        }
        if (locRes?.status === 200) {
          setLocations(locRes.data.docs || []);
        }
      } catch {
        // Reference filters are optional - ignore failures
      }
    };

    fetchReferenceData();
    return () => {
      active = false;
    };
  }, []);

  const buildItemsUrl = useCallback(
    (page) => {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", String(pagination.limit));
      if (categoryDoc?.id) {
        params.append("category", categoryDoc.id);
      }
      if (search) {
        params.append("q", search);
      }

      const appendEach = (key, values) => {
        (Array.isArray(values) ? values : [values]).forEach((value) => {
          if (value && value !== "All") {
            params.append(key, value);
          }
        });
      };

      appendEach("tier", selectedFilters.tier);
      appendEach("mediaType", selectedFilters.mediaType);

      if (Array.isArray(selectedFilters.illumination)) {
        const ids = selectedFilters.illumination
          .map((name) =>
            illuminations.find((i) => i.name === name)?.id
          )
          .filter(Boolean);
        ids.forEach((id) => params.append("illumination", id));
      }

      if (Array.isArray(selectedFilters.locations)) {
        const ids = selectedFilters.locations
          .map((label) =>
            locations.find((loc) => getLocationLabel(loc) === label)?.id
          )
          .filter(Boolean);
        ids.forEach((id) => params.append("location", id));
      }

      // Price range is single-select
      const priceValue = Array.isArray(selectedFilters.price)
        ? selectedFilters.price[0]
        : selectedFilters.price;
      if (priceValue && priceValue !== "All") {
        const bucket = PRICE_BUCKETS.find((b) => b.label === priceValue);
        if (bucket) {
          if (bucket.gte != null) params.append("price_gte", String(bucket.gte));
          if (bucket.lte != null) params.append("price_lte", String(bucket.lte));
        }
      }

      params.append("sort", SORT_PARAM_MAP[sortBy] || "rank");
      return `/api/outdoor-items/search?${params.toString()}`;
    },
    [categoryDoc, pagination.limit, search, selectedFilters, sortBy, illuminations, locations]
  );

  // Fetch items whenever filters/sort/search/page change
  useEffect(() => {
    if (!categoryDoc?.id) return;
    let active = true;
    const fetchItems = async () => {
      setItemsLoading(true);
      setItemsError(null);
      try {
        const response = await axios.get(buildItemsUrl(pagination.page));
        if (!active) return;
        if (response.status === 200) {
          const data = response.data;
          const fetched = data.docs || [];
          setItems((prev) =>
            pagination.page === 1 ? fetched : [...prev, ...fetched]
          );
          setPagination((prev) => ({
            ...prev,
            totalDocs: data.totalDocs ?? prev.totalDocs,
            totalPages: data.totalPages ?? prev.totalPages,
            page: data.page ?? prev.page,
          }));
        } else {
          setItemsError(response.data.message || "Failed to fetch items");
        }
      } catch (err) {
        if (!active) return;
        setItemsError(
          err.response?.data?.message ||
            "An error occurred while fetching items"
        );
      } finally {
        if (active) setItemsLoading(false);
      }
    };

    fetchItems();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryDoc?.id, selectedFilters, sortBy, search, pagination.page]);

  const sortOptions = useMemo(
    () => [
      { value: "rank", label: "Recommended" },
      { value: "name", label: "Sort by Name" },
      { value: "price_low", label: "Price: Low to High" },
      { value: "price_high", label: "Price: High to Low" },
    ],
    []
  );

  const handleSortClick = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (filterType, value) => {
    const filterValue = Array.isArray(value)
      ? value.length === 0
        ? null
        : value
      : value === "All"
        ? null
        : value;
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: filterValue,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const filterOptions = useMemo(() => {
    const options = {
      tier: ["All", ...TIER_VALUES],
      mediaType: ["All", ...MEDIA_TYPE_VALUES],
    };
    if (illuminations.length > 0) {
      options.illumination = [
        "All",
        ...illuminations.map((i) => i.name).filter(Boolean),
      ];
    }
    const locationLabels = Array.from(
      new Set(locations.map(getLocationLabel).filter(Boolean))
    ).sort();
    if (locationLabels.length > 0) {
      options.locations = ["All", ...locationLabels];
    }
    options.price = ["All", ...PRICE_BUCKETS.map((b) => b.label)];
    return options;
  }, [illuminations, locations]);

  const allItemsOnRequest =
    items.length > 0 &&
    items.every(
      (item) =>
        item.price_on_request ||
        !(Number(item.offer_rate) > 0 || Number(item.minimum_billing) > 0)
    );

  const heroImage =
    categoryDoc?.detailImage?.url ||
    categoryDoc?.detailImage?.cloudinary?.secure_url ||
    categoryDoc?.image?.url ||
    categoryDoc?.image?.cloudinary?.secure_url ||
    "/placeholder.jpg";

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !categoryDoc) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="text-red-500 text-xl">{error || "No details found"}</div>
      </div>
    );
  }

  return (
    <>
      <AdvertisingHeader
        category="outdoor"
        title={categoryDoc.title}
        sortOptions={sortOptions}
        sortBy={sortBy}
        handleSortClick={handleSortClick}
        showSortDropdown={showSortDropdown}
        setShowSortDropdown={setShowSortDropdown}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        showSearch
        searchValue={searchInput}
        onSearchChange={(value) => setSearchInput(value)}
        searchPlaceholder="Search outdoor sites..."
      />

      <div className="container mx-auto px-4 py-6">
        {/* Category hero */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-8">
            <img
              src={heroImage}
              alt={categoryDoc.title}
              className="w-full h-[240px] object-cover rounded-lg"
            />
            <div>
              <h1 className="text-3xl font-bold mb-4">{categoryDoc.title}</h1>
              {categoryDoc.description && (
                <p className="text-gray-600 mb-3 whitespace-pre-line">
                  {categoryDoc.description}
                </p>
              )}
              {categoryDoc.sub_description && (
                <p className="text-gray-500 mb-4 whitespace-pre-line text-sm">
                  {categoryDoc.sub_description}
                </p>
              )}
              {(allItemsOnRequest || items.length === 0) && (
                <button
                  onClick={() => setShowQuotationModal(true)}
                  className="inline-flex items-center px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                >
                  Get Quotation
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Items listing */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="text-gray-600">
            Showing {items.length}
            {pagination.totalDocs ? ` of ${pagination.totalDocs}` : ""} sites
          </p>
        </div>

        {itemsError && (
          <div className="text-center py-8 text-red-500">{itemsError}</div>
        )}

        {!itemsError && items.length === 0 && !itemsLoading && (
          <div className="text-center py-12">
            <p className="text-gray-600">No sites found matching your filters</p>
          </div>
        )}

        {items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((item) => (
              <div key={item.id}>
                <Card
                  data={{
                    ...item,
                    title: item.title,
                    price:
                      Number(item.offer_rate) > 0
                        ? item.offer_rate
                        : item.minimum_billing,
                  }}
                  category="outdoor"
                  options={false}
                />
              </div>
            ))}
          </div>
        )}

        {itemsLoading && (
          <div className="flex justify-center mt-8">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!itemsLoading && pagination.page < pagination.totalPages && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              className="flex items-center px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              Load More Sites
            </button>
          </div>
        )}
      </div>

      <BrandEndorsementQuotationModal
        brandEndorsementName={categoryDoc.title}
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        type="outdoor"
      />
    </>
  );
};

export default OutdoorItemsBrowser;
